import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { SITEDETAIL } from "@/constants/testIds";
import { Trash2, Cloud, AlertTriangle } from "lucide-react";

export default function DnsTab({ site }) {
  const [records, setRecords] = useState([]);
  const [type, setType] = useState("A");
  const [name, setName] = useState("");
  const [content, setContent] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/sites/${site.id}/dns`);
        if (!cancelled) { setRecords(r.data); setErr(""); }
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [site.id, refreshKey]);
  const reload = () => setRefreshKey((k) => k + 1);

  /**
   * Detect whether this tab is talking to real Cloudflare or the local Mongo
   * mock. Priority: (1) if any record ships `source: "cloudflare-live"`,
   * we're live; (2) if the site has a `cloudflare_zone_id`, the backend
   * WILL route to CF once we add a record (empty-list case); (3) fall back
   * to mocked.
   */
  const isLive = useMemo(() => {
    if (records.some((r) => r.source === "cloudflare-live")) return true;
    if (records.some((r) => r.source === "mocked")) return false;
    return Boolean(site.cloudflare_zone_id);
  }, [records, site.cloudflare_zone_id]);

  async function add() {
    if (!name || !content) return;
    setErr("");
    try {
      await api.post(`/sites/${site.id}/dns`, { type, name, content });
      setName(""); setContent("");
      reload();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  }
  async function remove(id) {
    setErr("");
    try {
      await api.delete(`/sites/${site.id}/dns/${id}`);
      reload();
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
  }

  return (
    <div className="space-y-4">
      <div className="border border-zinc-800 bg-zinc-900/40 rounded-sm p-5">
        <div className="label-tiny mb-3 flex items-center gap-2 flex-wrap">
          <span>Add DNS Record</span>
          {isLive ? (
            <span
              data-testid="dns-source-badge-live"
              className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-sm border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 normal-case tracking-normal"
            >
              <Cloud size={10} /> Live Cloudflare
            </span>
          ) : (
            <span
              data-testid="dns-source-badge-mock"
              className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 normal-case tracking-normal"
            >
              <AlertTriangle size={10} /> Mocked · site has no zone yet
            </span>
          )}
        </div>
        {!isLive && (
          <div className="mb-3 text-[11px] font-mono text-amber-300/80 leading-relaxed border border-amber-500/20 bg-amber-500/5 rounded-sm p-3">
            This site has no Cloudflare zone linked yet — records saved here stay in the local database and are <b>not</b> pushed to Cloudflare. Purchase or link a domain on the <b>Domain</b> tab to switch this tab to live Cloudflare.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-[110px_180px_1fr_auto] gap-2">
          <select
            data-testid={SITEDETAIL.dnsAddType}
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono"
          >
            {["A", "AAAA", "CNAME", "TXT", "MX", "NS"].map(t => <option key={t}>{t}</option>)}
          </select>
          <input
            data-testid={SITEDETAIL.dnsAddName}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="@ or sub"
            className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono"
          />
          <input
            data-testid={SITEDETAIL.dnsAddContent}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="value or IP"
            className="admin-input bg-transparent border border-zinc-800 px-3 py-2 rounded-sm text-sm mono"
          />
          <button
            data-testid={SITEDETAIL.dnsAddSubmit}
            onClick={add}
            className="bg-white text-zinc-950 hover:bg-zinc-200 px-4 py-2 rounded-sm text-sm font-semibold"
          >
            Add
          </button>
        </div>
        {err && (
          <div className="mt-3 text-xs mono text-red-300 border border-red-500/30 bg-red-500/5 rounded-sm p-2.5" data-testid="dns-error">
            {err}
          </div>
        )}
      </div>
      <div className="border border-zinc-800 rounded-sm">
        <div className="grid grid-cols-[80px_180px_1fr_80px_60px] bg-zinc-900/70 border-b border-zinc-800 label-tiny px-4 py-2">
          <div>Type</div><div>Name</div><div>Content</div><div>Proxied</div><div></div>
        </div>
        <div className="divide-y divide-zinc-800">
          {records.length === 0 && <div className="px-4 py-6 text-sm text-zinc-500">No DNS records yet.</div>}
          {records.map(r => (
            <div key={r.id} className="grid grid-cols-[80px_180px_1fr_80px_60px] items-center px-4 py-2 mono text-sm">
              <div className="text-amber-500">{r.type}</div>
              <div className="truncate text-zinc-300">{r.name}</div>
              <div className="truncate text-zinc-400">{r.content}</div>
              <div className="text-xs text-zinc-500">{r.proxied ? "yes" : "no"}</div>
              <div className="text-right"><button onClick={() => remove(r.id)} className="text-zinc-500 hover:text-red-400"><Trash2 size={14} /></button></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
